<template lang="pug">
//- DDBD's benchmark, ported from the DDBD docs' landing page
//- (docs-dadabada/.vitepress/theme/landing/PerfSection.vue). The numbers are
//- that page's perf.json, copied to src/assets/data/ddbd-perf.json: when DDBD
//- reruns its benchmark, copy the new file over. Tiles show the 1M-record
//- result; the chart shows the tag query as the database grows, for the share
//- of records the caller may read picked in the filter.
.bq-bench
    .bq-bench-head
        span.bq-bench-label Benchmark
        h3 Query Time Grows with the Answer, #[span.wordset Not with the Database]
        p How long DDBD, PostgreSQL, SQLite and MongoDB take to answer the same query as more records are stored.

    .bq-bench-filter(role="group" aria-label="Share of the records the caller may read")
        span.bq-bench-filter-lbl Query to be fetched by
        //- one group, so the four buttons wrap to the next line together
        .bq-bench-filter-btns
            button(
                v-for="l in LEVELS"
                :key="l.level"
                type="button"
                :class="{ on: level === l.level }"
                :aria-pressed="level === l.level"
                data-track="landing_ddbd_bench"
                :data-track-id="l.share"
                @click="level = l.level"
            ) {{ l.share }}
    p.bq-bench-note 100% is close to a full fetch with nothing filtered out. The low numbers are the queries that matter.

    .bq-bench-grid
        .bq-bench-tiles
            p.bq-bench-tiles-head 1,000,000&nbsp;records, median of {{ perf.repeats }}&nbsp;runs of {{ perf.runs }}&nbsp;queries
            .bq-bench-tile(v-for="s in SERIES" :key="s.key" :class="s.key")
                span.bq-bench-tile-name
                    i.mk(:class="s.key")
                    | {{ s.name }}
                span.bq-bench-tile-val {{ fmt(at1m[s.key]) }} #[small ms]
            p.bq-bench-tiles-foot
                template(v-if="footSlower.length") DDBD answered {{ joinList(footSlower.map((s) => `${ratio(at1m.dd, at1m[s.key])} faster than ${s.short}`)) }}.
                template(v-for="s in footFaster" :key="s.key") {{ (footSlower.length ? ' ' : '') + s.short }} answered {{ ratio(at1m[s.key], at1m.dd) }}&nbsp;faster than DDBD.

        figure.bq-bench-chart
            svg(:viewBox="`0 0 ${W} ${H}`" role="img" :aria-label="`Median query time by number of records, caller may read ${share}`")
                g.grid
                    g(v-for="t in ticks" :key="t")
                        line(:x1="L" :x2="L + PW" :y1="yOf(t)" :y2="yOf(t)")
                        text(:x="L - 10" :y="yOf(t) + 4" text-anchor="end") {{ tickLabel(t) }}
                    text.xl(v-for="(p, i) in points" :key="p.records" :x="xOf(i)" :y="H - 18" text-anchor="middle") {{ sizeLabel(p.records) }}{{ narrow ? '' : ' records' }}
                line.cross(v-if="hover !== null" :x1="xOf(hover)" :x2="xOf(hover)" :y1="T" :y2="T + PH")
                g.series(v-for="s in paths" :key="s.key" :class="s.key")
                    path.line(:d="s.d")
                    g(v-for="(p, i) in points" :key="i")
                        rect(v-if="s.key === 'dd'" :x="xOf(i) - 5" :y="yOf(p.dd) - 5" width="10" height="10")
                        circle(v-else-if="s.key === 'pg'" :cx="xOf(i)" :cy="yOf(p.pg)" r="5")
                        path(v-else-if="s.key === 'lite'" :d="`M${xOf(i)} ${yOf(p.lite) - 6} l6 10 h-12 z`")
                        path(v-else :d="`M${xOf(i)} ${yOf(p.mongo) - 6} l6 6 l-6 6 l-6 -6 z`")
                g.ends
                    g(v-for="e in endLabels" :key="e.key")
                        text.end-name(:class="e.key" :x="L + PW + 12" :y="e.y + 4") {{ e.short }}
                        text.end-val(:x="L + PW + 12" :y="e.y + 18") {{ fmt(e.v) }} ms
                rect.hit(
                    v-for="(p, i) in points"
                    :key="'hit' + i"
                    :x="xOf(i) - PW / 4"
                    :y="T"
                    :width="PW / 2"
                    :height="PH"
                    tabindex="0"
                    :aria-label="`${sizeLabel(p.records)} records`"
                    @mouseenter="hover = i"
                    @mouseleave="hover = null"
                    @focus="hover = i"
                    @blur="hover = null"
                )
            .bq-bench-tip(v-if="hover !== null" :style="tipStyle")
                strong {{ points[hover].records.toLocaleString('en') }} records
                span(v-for="s in SERIES" :key="s.key")
                    i.mk(:class="s.key")
                    | {{ s.short }}
                    b {{ fmt(points[hover][s.key]) }} ms
                span.tip-rows {{ points[hover].rows }} records per answer on average
            figcaption Median time per query, log scale, by number of records stored.

    details.bq-bench-more
        summary All results at 1,000,000 records
        .bq-bench-tbl
            table
                thead
                    tr
                        th Query
                        th Caller may read
                        th Records returned
                        th DDBD
                        th PostgreSQL
                        th SQLite
                        th MongoDB
                tbody
                    tr(v-for="r in perf.table" :key="r.kind + r.level")
                        td {{ r.kind === 'tag' ? 'tag sale, price >= X' : 'price >= X, no tag' }}
                        td {{ r.share }}
                        td {{ r.rows }}
                        td(:class="{ win: r.dd <= r.pg && r.dd <= r.lite && r.dd <= r.mongo }") {{ fmt(r.dd) }} ms
                        td {{ fmt(r.pg) }} ms
                        td {{ fmt(r.lite) }} ms
                        td {{ fmt(r.mongo) }} ms
        p.bq-bench-more-note
            | Without a tag, an index on price alone serves the query well once the user may read 10% or more of the
            | records, and PostgreSQL and SQLite answer faster than DDBD. With a tag, a SQL database has to combine
            | the tag index, the price order and the access filter at query time; DDBD wrote a key that already holds
            | all three when the record was saved. When the user may read every record there is no access filter to
            | combine, and PostgreSQL answers the tag query faster. MongoDB combines its indexes at query time as the
            | SQL databases do, so it slows down with the tag and the access filter too; without them, when the user
            | may read every record, it is faster than DDBD.
        p.bq-bench-more-note
            | Measured on one laptop with all data in memory, each database behind the same HTTP request and JSON
            | answer.
            |
            a(href="https://ddbd.skapi.com" target="_blank" rel="noopener" data-track="landing_ddbd_bench_method") How this was measured
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import perf from "@/assets/data/ddbd-perf.json";

type Key = "dd" | "pg" | "lite" | "mongo";
const SERIES: { key: Key; name: string; short: string }[] = [
    { key: "dd", name: perf.engines.dd, short: "DDBD" },
    { key: "pg", name: perf.engines.pg, short: "PostgreSQL" },
    { key: "lite", name: perf.engines.lite, short: "SQLite" },
    { key: "mongo", name: perf.engines.mongo, short: "MongoDB" },
];
const LEVELS = [
    { level: "0", share: "0.1%" },
    { level: "1", share: "1%" },
    { level: "2", share: "10%" },
    { level: "3", share: "100%" },
];
const level = ref("1");
const points = computed(() => (perf.scaling as Record<string, any[]>)[level.value]);
const at1m = computed(() => points.value[points.value.length - 1]);
const share = computed(() => LEVELS.find((l) => l.level === level.value)!.share);
const fmt = (v: number) => (v >= 100 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(2));
// one decimal below 10x, so a ratio is never rounded up into a bigger claim
const ratio = (a: number, b: number) => (b / a >= 10 ? `${Math.round(b / a)}×` : `${(b / a).toFixed(1)}×`);
const joinList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
// the line under the tiles: DDBD against each other database, whichever way round it went
const footSlower = computed(() => SERIES.filter((s) => s.key !== "dd" && at1m.value[s.key] > at1m.value.dd));
const footFaster = computed(() => SERIES.filter((s) => s.key !== "dd" && at1m.value[s.key] <= at1m.value.dd));

// chart geometry: log y, three evenly spaced sizes on x. Phones get a narrower
// drawing, so its text stays readable.
const narrow = ref(false);
let mq: MediaQueryList | null = null;
const onMq = () => (narrow.value = !!mq?.matches);
onMounted(() => {
    mq = window.matchMedia("(max-width: 640px)");
    onMq();
    mq.addEventListener("change", onMq);
});
onBeforeUnmount(() => mq?.removeEventListener("change", onMq));
const W = computed(() => (narrow.value ? 380 : 640));
const L = computed(() => (narrow.value ? 52 : 64));
const R = computed(() => (narrow.value ? 96 : 150));
const H = 340, T = 18, B = 46;
const PW = computed(() => W.value - L.value - R.value);
const PH = H - T - B;
const allVals = computed(() => points.value.flatMap((p: any) => SERIES.map((s) => p[s.key] as number)));
const lo = computed(() => Math.pow(10, Math.floor(Math.log10(Math.min(...allVals.value)))));
const hi = computed(() => Math.pow(10, Math.ceil(Math.log10(Math.max(...allVals.value)))));
const xOf = (i: number) => L.value + (i * PW.value) / 2;
const yOf = (v: number) => T + ((Math.log10(hi.value) - Math.log10(v)) / (Math.log10(hi.value) - Math.log10(lo.value))) * PH;
const ticks = computed(() => {
    const out: number[] = [];
    for (let v = lo.value; v <= hi.value * 1.001; v *= 10) out.push(v);
    return out;
});
const tickLabel = (v: number) => (v < 1 ? `${v} ms` : `${Math.round(v)} ms`);
const paths = computed(() =>
    SERIES.map((s) => ({ ...s, d: points.value.map((p: any, i: number) => `${i ? "L" : "M"}${xOf(i)} ${yOf(p[s.key])}`).join(" ") })),
);
// end labels, pushed apart when they would overlap
const endLabels = computed(() => {
    const last = points.value[points.value.length - 1];
    const ls = SERIES.map((s) => ({ ...s, v: last[s.key] as number, y: yOf(last[s.key]) })).sort((a, b) => a.y - b.y);
    for (let i = 1; i < ls.length; i++) if (ls[i].y - ls[i - 1].y < 30) ls[i].y = ls[i - 1].y + 30;
    // pushed below the plot: move the whole stack up so the last label stays inside
    const over = ls[ls.length - 1].y - (T + PH);
    if (over > 0) ls.forEach((l) => (l.y -= over));
    return ls;
});
const sizeLabel = (n: number) => (n >= 1e6 ? `${n / 1e6}M` : `${n / 1e3}k`);
const hover = ref<number | null>(null);
// the tooltip opens to the right of the first point, to the left of the last, centred otherwise
const tipStyle = computed(() => {
    if (hover.value === null) return {};
    const left = `${(xOf(hover.value) / W.value) * 100}%`;
    const shift = hover.value === 0 ? "translateX(12px)" : hover.value === points.value.length - 1 ? "translateX(calc(-100% - 12px))" : "translateX(-50%)";
    return { left, transform: shift };
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

/* DDBD in the brand blue; the other three in greys, told apart by their
   markers and Mongo's dashes as well as by shade */
.bq-bench {
    --bb-dd: var(--bq-pink);
    --bb-pg: #5c6578;
    --bb-lite: #a3abbb;
    --bb-mongo: #7f889a;
    margin-top: 2.5rem;
    padding: 2rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

[data-theme="dark"] .bq-bench,
.theme-dark .bq-bench {
    --bb-pg: #a3abbb;
    --bb-lite: #5f687a;
    --bb-mongo: #828b9d;
}

.bq-bench-head {
    h3 {
        margin: 0 0 0.6rem;
        font-size: 1.15rem;
        line-height: 1.35;
        font-weight: 700;
    }

    p {
        max-width: 46rem;
        margin: 0 0 1.5rem;
        color: @muted;
        line-height: 1.6;
    }
}

.bq-bench-label {
    display: block;
    margin-bottom: 0.35rem;
    color: @pink;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.bq-bench-filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    button {
        padding: 0.55rem 0.85rem;
        border: 1px solid @line;
        background: @paper;
        color: @ink;
        font: 600 0.8rem/1 @mono;
        cursor: pointer;

        &.on {
            border-color: @pink;
            background: @pink;
            color: @paper;
        }
    }

    @media (hover: hover) and (pointer: fine) {
        button:not(.on):hover {
            border-color: @pink;
        }
    }
}

.bq-bench-filter-btns {
    display: flex;
    flex-wrap: nowrap;
    gap: 0.5rem;

    button {
        white-space: nowrap;
    }
}

.bq-bench-filter-lbl {
    margin-right: 0.35rem;
    white-space: nowrap;
    color: @muted;
    font-size: 0.8rem;
}

.bq-bench-note {
    max-width: 46rem;
    margin: 0 0 1.75rem;
    color: @muted;
    font-size: 0.85rem;
    line-height: 1.6;
}

.bq-bench-grid {
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.6fr);
    gap: 2rem;
    align-items: start;
}

.bq-bench-tiles {
    display: grid;
    gap: 0.75rem;
}

.bq-bench-tiles-head {
    margin: 0 0 0.25rem;
    color: @muted;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.bq-bench-tile {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.9rem 1.1rem;
    border: 1px solid @line;
    background: @paper;

    &.dd {
        border-color: @pink;
        background: @pink-bg;
    }
}

.bq-bench-tile-name {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    color: @muted;
    font-size: 0.8rem;
    white-space: nowrap;

    .dd & {
        color: @ink;
        font-weight: 600;
    }
}

.bq-bench-tile-val {
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    white-space: nowrap;

    .bq-bench-tile:not(.dd) & {
        color: @muted;
        font-size: 1.4rem;
        font-weight: 600;
    }

    .dd & {
        color: @pink;
    }

    small {
        color: @muted;
        font-size: 0.85rem;
        font-weight: 600;
    }
}

.bq-bench-tiles-foot {
    margin: 0.5rem 0 0;
    color: @muted;
    font-size: 0.85rem;
    line-height: 1.6;
}

.mk {
    display: inline-block;
    flex: none;
    width: 10px;
    height: 10px;

    &.dd {
        background: var(--bb-dd);
    }

    &.pg {
        background: var(--bb-pg);
        border-radius: 50%;
    }

    &.lite {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 10px solid var(--bb-lite);
    }

    &.mongo {
        width: 9px;
        height: 9px;
        background: var(--bb-mongo);
        transform: rotate(45deg);
    }
}

.bq-bench-chart {
    position: relative;
    margin: 0;
    padding: 1.25rem 1.25rem 1rem;
    border: 1px solid @line;
    background: @paper;

    svg {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
        font-family: @mono;
    }

    figcaption {
        margin-top: 0.75rem;
        color: @muted;
        font-size: 0.8rem;
        line-height: 1.6;
    }
}

.grid {
    line {
        stroke: @line;
        stroke-width: 1;
    }

    text {
        fill: @muted;
        font-size: 12px;
    }

    .xl {
        fill: @ink;
        font-size: 12.5px;
    }
}

.cross {
    stroke: @ink;
    stroke-width: 1;
}

.series {
    .line {
        fill: none;
        stroke-width: 2;
        stroke-linejoin: round;
    }

    &.dd .line {
        stroke: var(--bb-dd);
        stroke-width: 3;
    }

    &.pg .line {
        stroke: var(--bb-pg);
    }

    &.lite .line {
        stroke: var(--bb-lite);
    }

    &.mongo .line {
        stroke: var(--bb-mongo);
        stroke-dasharray: 6 4;
    }

    rect,
    circle,
    g path {
        stroke: @paper;
        stroke-width: 2;
    }

    &.dd rect {
        fill: var(--bb-dd);
    }

    &.pg circle {
        fill: var(--bb-pg);
    }

    &.lite g path {
        fill: var(--bb-lite);
    }

    &.mongo g path {
        fill: var(--bb-mongo);
    }
}

.end-name {
    fill: @ink;
    font-size: 12.5px;
    font-weight: 700;

    &.dd {
        fill: var(--bb-dd);
    }
}

.end-val {
    fill: @muted;
    font-size: 12px;
}

.hit {
    fill: transparent;
    outline: none;
    cursor: crosshair;
}

.bq-bench-tip {
    position: absolute;
    top: 1.5rem;
    display: grid;
    gap: 0.25rem;
    min-width: 210px;
    padding: 0.6rem 0.75rem;
    border: 1px solid @ink;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    font-size: 0.8rem;
    pointer-events: none;

    span {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    b {
        margin-left: auto;
    }

    .tip-rows {
        color: @muted;
        font-size: 0.72rem;
    }
}

.bq-bench-more {
    margin-top: 1.75rem;
    padding-top: 1.1rem;
    border-top: 1px solid @line;

    summary {
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
    }
}

.bq-bench-tbl {
    margin-top: 1rem;
    overflow-x: auto;

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
        font-variant-numeric: tabular-nums;
    }

    th {
        padding: 0.6rem 0.75rem;
        border-bottom: 1px solid @ink;
        color: @muted;
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-align: left;
        text-transform: uppercase;
        white-space: nowrap;
    }

    td {
        padding: 0.6rem 0.75rem;
        border-bottom: 1px solid @line;
        white-space: nowrap;

        &.win {
            background: @pink;
            color: @paper;
            font-weight: 700;
        }
    }
}

.bq-bench-more-note {
    max-width: 52rem;
    margin: 1rem 0 0;
    color: @muted;
    font-size: 0.85rem;
    line-height: 1.7;

    a {
        color: @pink;
    }
}

@media (max-width: 900px) {
    .bq-bench-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 640px) {
    .bq-bench {
        padding: 1.25rem 1rem;
    }

    .bq-bench-chart {
        padding: 0.75rem;
    }

    .bq-bench-tip {
        min-width: 180px;
    }
}
</style>
